/**
 * Scan a transparent PNG for connected regions of transparent pixels and
 * return their bounding boxes in 0-1 page coordinates. Used by the admin
 * template uploader to propose photo slots automatically.
 *
 * Approach:
 *   1. Decode the PNG into an OffscreenCanvas at native resolution.
 *   2. Threshold: alpha < 32 → "transparent" / 1 in the bitmap.
 *   3. Flood-fill (4-connectivity) to find connected components.
 *   4. For each component, compute its bounding rectangle.
 *   5. Filter out rectangles smaller than `minAreaFraction` of the page
 *      (default 5%) — these are anti-aliasing fringes, not photo holes.
 *   6. Sort top-to-bottom, then left-to-right (reading order).
 *
 * Returns boxes in 0-1 coords relative to the PNG's pixel dimensions.
 */

export interface DetectedSlot {
  x: number;       // 0-1
  y: number;       // 0-1
  width: number;   // 0-1
  height: number;  // 0-1
}

export interface AutoDetectOptions {
  /** Pixels with alpha BELOW this are treated as transparent. Default 32. */
  alphaThreshold?: number;
  /** Components covering less than this fraction of the image are dropped. */
  minAreaFraction?: number;
}

export async function detectTransparentSlots(
  source: File | string,
  options: AutoDetectOptions = {},
): Promise<DetectedSlot[]> {
  const alphaThreshold = options.alphaThreshold ?? 32;
  const minAreaFraction = options.minAreaFraction ?? 0.05;

  const bitmap = await loadBitmap(source);
  const W = bitmap.width;
  const H = bitmap.height;
  if (W === 0 || H === 0) return [];

  const canvas: HTMLCanvasElement | OffscreenCanvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(W, H)
      : (() => {
          const c = document.createElement('canvas');
          c.width = W;
          c.height = H;
          return c;
        })();
  const ctx = (canvas as HTMLCanvasElement).getContext('2d', {
    willReadFrequently: true,
  } as CanvasRenderingContext2DSettings) as
    | CanvasRenderingContext2D
    | OffscreenCanvasRenderingContext2D
    | null;
  if (!ctx) return [];
  ctx.drawImage(bitmap as CanvasImageSource, 0, 0);

  const img = ctx.getImageData(0, 0, W, H);
  const data = img.data;
  // 1 = transparent pixel that hasn't been claimed yet; -1 = claimed.
  const map = new Int8Array(W * H);
  for (let i = 0; i < W * H; i++) {
    if (data[i * 4 + 3] < alphaThreshold) map[i] = 1;
  }

  const minArea = Math.floor(W * H * minAreaFraction);
  const slots: DetectedSlot[] = [];
  const stack: number[] = [];

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const start = y * W + x;
      if (map[start] !== 1) continue;

      let minX = x, maxX = x, minY = y, maxY = y, area = 0;
      stack.push(start);
      map[start] = -1;
      while (stack.length) {
        const idx = stack.pop()!;
        const px = idx % W;
        const py = (idx - px) / W;
        area++;
        if (px < minX) minX = px;
        if (px > maxX) maxX = px;
        if (py < minY) minY = py;
        if (py > maxY) maxY = py;

        const neighbours = [
          idx - 1,
          idx + 1,
          idx - W,
          idx + W,
        ];
        for (const n of neighbours) {
          if (n < 0 || n >= W * H) continue;
          // Skip horizontal wrap-around: idx-1 from x=0 lands on the
          // previous row's last column.
          if (n === idx - 1 && px === 0) continue;
          if (n === idx + 1 && px === W - 1) continue;
          if (map[n] === 1) {
            map[n] = -1;
            stack.push(n);
          }
        }
      }

      if (area < minArea) continue;
      const w = maxX - minX + 1;
      const h = maxY - minY + 1;
      slots.push({
        x: minX / W,
        y: minY / H,
        width: w / W,
        height: h / H,
      });
    }
  }

  // Reading order: top → bottom, then left → right.
  slots.sort((a, b) => {
    const dy = a.y - b.y;
    if (Math.abs(dy) > 0.05) return dy;
    return a.x - b.x;
  });

  return slots;
}

async function loadBitmap(source: File | string): Promise<ImageBitmap | HTMLImageElement> {
  if (source instanceof File) {
    return createImageBitmap(source);
  }
  const res = await fetch(source, { mode: 'cors' });
  if (!res.ok) throw new Error(`Failed to fetch ${source}: ${res.status}`);
  const blob = await res.blob();
  return createImageBitmap(blob);
}
